import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getPolicyDocuments } from '../content/policies';
import type { PolicySection } from '../content/policies/types';

export const TermsOfService: React.FC = () => {
  const [searchParams] = useSearchParams();
  const locale = searchParams.get('locale') || 'en';
  const { terms } = getPolicyDocuments(locale);

  const renderSection = (section: PolicySection, index: number) => (
    <section key={index} className="mb-8">
      <h2 className="text-2xl font-semibold text-gray-900 mb-4">{section.title}</h2>
      {section.body.map((paragraph, idx) => (
        <p key={idx} className="text-gray-700 mb-4">
          {paragraph.includes('Contact Us page') ? (
            <>
              If you have any questions about these Terms of Service, please contact us via the{' '}
              <Link to="/contact-us" className="text-blue-600 hover:text-blue-800 underline">
                Contact Us page
              </Link>
              {' '}on our Platform.
            </>
          ) : (
            paragraph
          )}
        </p>
      ))}
      {section.bulletPoints && section.bulletPoints.length > 0 && (
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
          {section.bulletPoints.map((item, bulletIdx) => (
            <li key={bulletIdx}>{item}</li>
          ))}
        </ul>
      )}
    </section>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-2 rounded-lg">
                <span className="text-white font-bold text-lg">ML</span>
              </div>
              <div className="ml-3">
                <h1 className="text-xl font-semibold text-gray-800">Magic Links ✨</h1>
              </div>
            </div>
            <Link
              to="/"
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition duration-200"
            >
              <ArrowLeft size={20} />
              <span>Back</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">{terms.title}</h1>
          
          <div className="prose max-w-none">
            <p className="text-gray-600 mb-6">
              {terms.lastUpdatedLabel}
            </p>

            {terms.sections.map((section, index) => renderSection(section, index))}
          </div>
        </div>
      </div>
    </div>
  );
};
